import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home, Calendar, Clock, User, MessageSquare, Award } from 'lucide-react';
import './ArchivePage.css';

const ArchivedDebateView = () => {
  const { debateId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [debate, setDebate] = useState(null);
  const [debateArguments, setDebateArguments] = useState([]);
  const [participantNames, setParticipantNames] = useState({ a: '', b: '' });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchDebateDetails();
  }, [debateId]);

  const fetchDebateDetails = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/archive/view/${debateId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch debate details');
      }

      const data = await response.json();
      setDebate(data.debate);
      setDebateArguments(data.arguments || []);
      setParticipantNames({
        a: data.participant_a_name || 'User A',
        b: data.participant_b_name || 'User B'
      });
    } catch (error) {
      console.error('Error fetching debate details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="archive-page">
        <div className="archive-loading">Lade Debatte...</div>
      </div>
    );
  }

  if (!debate) {
    return (
      <div className="archive-page">
        <div className="archive-container">
          <h1>Debatte nicht gefunden</h1>
          <Button onClick={() => navigate('/archive')}>
            Zurück zum Archiv
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="archive-page">
      <div className="archive-container">
        {/* Header */}
        <div className="archive-header">
          <Button
            variant="outline"
            onClick={() => navigate('/archive')}
            className="home-button"
          >
            <Home className="mr-2 h-4 w-4" />
            Zurück zum Archiv
          </Button>
        </div>

        {/* Debate Info Card */}
        <Card className="debate-detail-card" style={{ marginBottom: '30px' }}>
          <CardContent style={{ padding: '30px' }}>
            <h1 className="debate-thesis" style={{ marginBottom: '20px' }}>{debate.thesis}</h1>
            
            <div className="debate-metadata">
              <div className="metadata-item">
                <User size={16} />
                <span>{participantNames.a} vs {participantNames.b}</span>
              </div>
              
              <div className="metadata-item">
                <Calendar size={16} />
                <span>{formatDate(debate.created_at)}</span>
              </div>
              
              <div className="metadata-item">
                <Clock size={16} />
                <span>{formatTime(debate.created_at)}</span>
              </div>
              
              <div className="metadata-item">
                <Award size={16} />
                <span>Score: {debate.score_a || 0}:{debate.score_b || 0}</span>
              </div>
              
              <div className="metadata-item">
                <MessageSquare size={16} />
                <span>{debateArguments.length} Argumente</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Arguments List */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ 
            fontFamily: "'Bebas Neue', sans-serif", 
            fontSize: '32px', 
            color: 'white', 
            marginBottom: '20px' 
          }}>
            Debattenverlauf
          </h2>
          
          {debateArguments.length === 0 ? (
            <Card>
              <CardContent style={{ padding: '40px', textAlign: 'center' }}>
                <p style={{ fontFamily: 'Abel', color: '#666' }}>
                  Keine Argumente in dieser Debatte.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {debateArguments.map((arg, index) => (
                <Card key={arg.id || index} style={{ 
                  background: arg.participant_role === 'protagonist_a' ? '#f0f7ff' : '#fff5f0',
                  border: `2px solid ${arg.participant_role === 'protagonist_a' ? '#667eea' : '#ff6b6b'}`
                }}>
                  <CardContent style={{ padding: '20px' }}>
                    {/* Argument Header */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '15px'
                    }}>
                      <div style={{ 
                        fontFamily: "'Bebas Neue', sans-serif", 
                        fontSize: '20px',
                        color: arg.participant_role === 'protagonist_a' ? '#667eea' : '#ff6b6b'
                      }}>
                        {arg.participant_role === 'protagonist_a' ? participantNames.a : participantNames.b}
                      </div>
                      <div style={{ 
                        fontFamily: 'Abel', 
                        fontSize: '14px', 
                        color: '#999' 
                      }}>
                        {formatTime(arg.timestamp)}
                      </div>
                    </div>

                    {/* Argument Content */}
                    <div style={{ 
                      fontFamily: 'Abel', 
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      color: '#333',
                      marginBottom: '15px'
                    }}>
                      {arg.content}
                    </div>

                    {/* Score Display */}
                    {arg.moderation_result && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        padding: '10px',
                        background: 'rgba(255,255,255,0.5)',
                        borderRadius: '8px'
                      }}>
                        <Award size={16} style={{ color: '#ffc107' }} />
                        <span style={{ 
                          fontFamily: 'Abel', 
                          fontSize: '14px',
                          fontWeight: '600'
                        }}>
                          Punkte: {arg.moderation_result.score?.total_score || 0}
                        </span>
                        {arg.moderation_result.summary && (
                          <span style={{ 
                            fontFamily: 'Abel', 
                            fontSize: '13px',
                            color: '#666',
                            marginLeft: '10px'
                          }}>
                            {arg.moderation_result.summary.substring(0, 100)}...
                          </span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchivedDebateView;
